import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { loginWithGoogle } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Check, Clock, MapPin, CalendarDays, User, ClipboardCheck,
  ChevronRight, ChevronLeft, Loader2, Sparkles,
} from "lucide-react";

const TIME_SLOTS = ["8:00 AM", "10:00 AM", "12:00 PM", "2:00 PM", "4:00 PM"];
const RECURRING = [
  { v: "one-time", l: "One-time" },
  { v: "weekly", l: "Weekly" },
  { v: "biweekly", l: "Bi-weekly" },
  { v: "monthly", l: "Monthly" },
];
const STEPS = ["Service", "Location", "Schedule", "Details", "Confirm"];

export default function Booking() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [services, setServices] = useState([]);
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState(null);
  const [address, setAddress] = useState("");
  const [zip, setZip] = useState("");
  const [zipStatus, setZipStatus] = useState(null); // null | true | false
  const [checkingZip, setCheckingZip] = useState(false);
  const [date, setDate] = useState(null);
  const [timeSlot, setTimeSlot] = useState("");
  const [recurring, setRecurring] = useState("one-time");
  const [notes, setNotes] = useState("");
  const [contact, setContact] = useState({ name: "", phone: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const designId = params.get("design");

  useEffect(() => {
    api.get("/services").then((r) => {
      setServices(r.data);
      const pre = params.get("service");
      if (pre) {
        const found = r.data.find((s) => s.service_id === pre);
        if (found) { setSelected(found); setStep(1); }
      }
    }).catch(() => {});
  }, [params]);

  useEffect(() => {
    if (user) {
      setContact((c) => ({ name: c.name || user.name || "", phone: c.phone || user.phone || "", email: c.email || user.email || "" }));
      if (user.address) setAddress((a) => a || user.address);
    }
  }, [user]);

  const checkZip = async (value) => {
    setZip(value);
    setZipStatus(null);
    const cleaned = value.replace(/\s/g, "");
    if (cleaned.length >= 3) {
      setCheckingZip(true);
      try {
        const res = await api.get(`/service-area/validate/${encodeURIComponent(cleaned)}`);
        setZipStatus(res.data.served);
      } catch {
        setZipStatus(false);
      } finally {
        setCheckingZip(false);
      }
    }
  };

  const canNext = () => {
    if (step === 0) return !!selected;
    if (step === 1) return address.trim().length > 3 && zipStatus === true;
    if (step === 2) return !!date && !!timeSlot;
    if (step === 3) return contact.name && contact.phone && contact.email && !!user;
    return true;
  };

  const submit = async () => {
    if (!user) { loginWithGoogle("/booking"); return; }
    setSubmitting(true);
    try {
      const payload = {
        service_id: selected.service_id,
        service_name: selected.name,
        category: selected.category,
        price: selected.price,
        duration: selected.duration,
        date: date.toISOString().slice(0, 10),
        time_slot: timeSlot,
        address,
        zip_code: zip,
        notes,
        recurring,
        contact_name: contact.name,
        contact_phone: contact.phone,
        contact_email: contact.email,
        design_id: designId || null,
      };
      await api.post("/bookings", payload);
      toast.success("Booking confirmed! We'll send a confirmation shortly.");
      navigate("/portal");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete booking.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      <section className="bg-[#1A362D] py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8FA38C]">Online Booking</span>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight">Schedule your service</h1>
          {designId && (
            <p className="mt-3 inline-flex items-center gap-2 text-sm text-[#C47B57] bg-white/5 px-3 py-1.5 rounded-full">
              <Sparkles className="h-4 w-4" /> Linked to your saved AI design
            </p>
          )}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        {/* Stepper */}
        <div className="flex items-center justify-between mb-10" data-testid="booking-stepper">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center">
                <span className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-medium transition-colors ${
                  i < step ? "bg-[#8FA38C] text-white" : i === step ? "bg-[#1A362D] text-white" : "bg-[#E2E8E4] text-[#8A9A8C]"
                }`}>
                  {i < step ? <Check className="h-4 w-4" /> : i + 1}
                </span>
                <span className={`mt-1.5 text-[11px] hidden sm:block ${i === step ? "text-[#1A362D] font-medium" : "text-[#8A9A8C]"}`}>{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 mx-2 ${i < step ? "bg-[#8FA38C]" : "bg-[#E2E8E4]"}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-[#E2E8E4]/60 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-10">
          {/* STEP 0: Service */}
          {step === 0 && (
            <div data-testid="step-service">
              <h2 className="font-heading text-2xl font-semibold text-[#1A362D] flex items-center gap-2"><ClipboardCheck className="h-6 w-6 text-[#C47B57]" /> Select a service</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
                {services.map((s) => (
                  <button
                    key={s.service_id}
                    onClick={() => setSelected(s)}
                    data-testid={`select-service-${s.service_id}`}
                    className={`text-left p-5 rounded-xl border transition-all ${
                      selected?.service_id === s.service_id ? "border-[#C47B57] ring-1 ring-[#C47B57] bg-[#C47B57]/5" : "border-[#E2E8E4] hover:border-[#8FA38C]"
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <p className="font-medium text-[#1A362D]">{s.name}</p>
                      <span className="text-[#C47B57] font-heading font-semibold">${s.price}</span>
                    </div>
                    <p className="text-xs text-[#8A9A8C] mt-1 capitalize">{s.category} · {s.duration}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 1: Location */}
          {step === 1 && (
            <div data-testid="step-location">
              <h2 className="font-heading text-2xl font-semibold text-[#1A362D] flex items-center gap-2"><MapPin className="h-6 w-6 text-[#C47B57]" /> Service address</h2>
              <div className="mt-6 space-y-5">
                <div>
                  <Label className="text-[#1A362D]">Street address</Label>
                  <Input data-testid="booking-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="123 Garden Lane" className="mt-1.5 bg-[#FAFAFA] border-[#E2E8E4]" />
                </div>
                <div>
                  <Label className="text-[#1A362D]">Postal code</Label>
                  <Input data-testid="booking-zip" value={zip} maxLength={7} onChange={(e) => checkZip(e.target.value.toUpperCase().slice(0, 7))} placeholder="T2P 3G7" className="mt-1.5 bg-[#FAFAFA] border-[#E2E8E4]" />
                  {checkingZip && <p className="mt-2 text-sm text-[#8A9A8C] flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking coverage…</p>}
                  {zipStatus === true && <p className="mt-2 text-sm text-[#3B7A57] flex items-center gap-1.5" data-testid="zip-served"><Check className="h-4 w-4" /> Great — we service this area!</p>}
                  {zipStatus === false && <p className="mt-2 text-sm text-[#C0492B]" data-testid="zip-not-served">Sorry, we don't cover this postal code yet. We serve Calgary, Airdrie & Okotoks (e.g. T2P, T4A, T1S).</p>}
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Schedule */}
          {step === 2 && (
            <div data-testid="step-schedule">
              <h2 className="font-heading text-2xl font-semibold text-[#1A362D] flex items-center gap-2"><CalendarDays className="h-6 w-6 text-[#C47B57]" /> Pick a date & time</h2>
              <div className="mt-6 grid md:grid-cols-2 gap-8">
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    className="rounded-xl border border-[#E2E8E4]"
                    data-testid="booking-calendar"
                  />
                </div>
                <div className="space-y-5">
                  <div>
                    <Label className="text-[#1A362D] flex items-center gap-1.5"><Clock className="h-4 w-4" /> Time slot</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {TIME_SLOTS.map((t) => (
                        <button
                          key={t}
                          onClick={() => setTimeSlot(t)}
                          data-testid={`time-${t.replace(/[:\s]/g, "")}`}
                          className={`py-2.5 rounded-lg border text-sm transition-all ${timeSlot === t ? "border-[#C47B57] bg-[#C47B57]/5 text-[#1A362D] font-medium" : "border-[#E2E8E4] text-[#4A5D4E] hover:border-[#8FA38C]"}`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#1A362D]">Frequency</Label>
                    <Select value={recurring} onValueChange={setRecurring}>
                      <SelectTrigger className="mt-1.5 bg-[#FAFAFA] border-[#E2E8E4]" data-testid="booking-recurring"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {RECURRING.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {recurring !== "one-time" && <p className="mt-2 text-xs text-[#8FA38C]">Recurring maintenance plan — cancel anytime.</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Details */}
          {step === 3 && (
            <div data-testid="step-details">
              <h2 className="font-heading text-2xl font-semibold text-[#1A362D] flex items-center gap-2"><User className="h-6 w-6 text-[#C47B57]" /> Your details</h2>
              {!user ? (
                <div className="mt-6 text-center p-8 rounded-xl bg-[#F2F4F2]">
                  <p className="text-[#4A5D4E]">Please sign in to complete your booking and manage it later.</p>
                  <Button onClick={() => loginWithGoogle("/booking")} data-testid="booking-signin-btn" className="mt-4 bg-[#1A362D] hover:bg-[#254A3E] text-white rounded-full px-8">
                    Sign in with Google
                  </Button>
                </div>
              ) : (
                <div className="mt-6 space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#1A362D]">Full name</Label>
                      <Input data-testid="booking-name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className="mt-1.5 bg-[#FAFAFA] border-[#E2E8E4]" />
                    </div>
                    <div>
                      <Label className="text-[#1A362D]">Phone</Label>
                      <Input data-testid="booking-phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className="mt-1.5 bg-[#FAFAFA] border-[#E2E8E4]" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#1A362D]">Email</Label>
                    <Input data-testid="booking-email" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="mt-1.5 bg-[#FAFAFA] border-[#E2E8E4]" />
                  </div>
                  <div>
                    <Label className="text-[#1A362D]">Notes (optional)</Label>
                    <Textarea data-testid="booking-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Gate codes, pets, special requests…" rows={3} className="mt-1.5 bg-[#FAFAFA] border-[#E2E8E4]" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 4: Confirm */}
          {step === 4 && (
            <div data-testid="step-confirm">
              <h2 className="font-heading text-2xl font-semibold text-[#1A362D] flex items-center gap-2"><ClipboardCheck className="h-6 w-6 text-[#C47B57]" /> Review & confirm</h2>
              <div className="mt-6 divide-y divide-[#F2F4F2] rounded-xl border border-[#E2E8E4] overflow-hidden">
                {[
                  ["Service", selected?.name],
                  ["Price", selected ? `$${selected.price} ${selected.price_unit}` : ""],
                  ["Address", `${address}, ${zip}`],
                  ["Date", date ? date.toDateString() : ""],
                  ["Time", timeSlot],
                  ["Frequency", RECURRING.find((r) => r.v === recurring)?.l],
                  ["Contact", `${contact.name} · ${contact.phone}`],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between px-5 py-3.5">
                    <span className="text-sm text-[#8A9A8C]">{k}</span>
                    <span className="text-sm font-medium text-[#1A362D] text-right">{v}</span>
                  </div>
                ))}
              </div>
              {designId && <p className="mt-4 text-sm text-[#C47B57] flex items-center gap-2"><Sparkles className="h-4 w-4" /> Your AI design will be attached to this request.</p>}
            </div>
          )}

          {/* Nav buttons */}
          <div className="mt-10 flex justify-between">
            <Button
              variant="ghost"
              onClick={() => setStep(Math.max(0, step - 1))}
              disabled={step === 0}
              data-testid="booking-back-btn"
              className="text-[#1A362D] disabled:opacity-30"
            >
              <ChevronLeft className="mr-1 h-4 w-4" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canNext()}
                data-testid="booking-next-btn"
                className="bg-[#1A362D] hover:bg-[#254A3E] text-white rounded-full px-8 disabled:opacity-40"
              >
                Continue <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={submit}
                disabled={submitting}
                data-testid="booking-confirm-btn"
                className="bg-[#C47B57] hover:bg-[#D58C68] text-white rounded-full px-8"
              >
                {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Confirming…</> : "Confirm Booking"}
              </Button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
